import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useMyQuery } from '@hooks/useMyQuery';
import { useStore } from '@hooks/useStore';
import { useGetBrandData } from '@hooks/useGetBrandData';
import { AppLoader } from '@components/Core/AppLoader';
import { PageHeader } from '@components/Core/PageHeader';
import Popup from '@components/Core/Popup/Popup';
import { SECOND_POPUP_WIDTH } from '@components/Core/Popup/constants';
import { ConfirmationPopup } from '@popups/ConfirmationPopup';
import ProductForm from '@forms/ProductForm';
import TrashIcon from '@icons/trash_icon.svg?component';
import { StatusType } from '@/types/common';
import { ProductsTableTransFields } from '@tables/ProductsTable/types';
import { productsTableTranslations } from '@tables/ProductsTable/translations';
import { EditProductTransFields } from './types';
import { editProductTranslations } from './translations';

function EditProduct() {
  const {
    brandsStore: { brand },
    productsStore: { getProduct, product, deleteProduct },
    popupStore: { openPopup, popupName, closePopup },
    alertStore: { showAlert },
  } = useStore();

  const { t } = useMyTranslations<
    EditProductTransFields | Extract<ProductsTableTransFields, 'deleteProduct' | 'confirmationText'>
  >([editProductTranslations.name, productsTableTranslations.name]);

  const { brandId, productId } = useParams();
  const [params, _] = useSearchParams();
  const navigate = useNavigate();

  // use addintional flag because use same "confirmation" popup here and in children
  const [available, setAvailable] = useState(false);

  const { isFetching, text } = useGetBrandData(t('edit product'));

  const { isFetching: isFetchingPoint } = useMyQuery({
    queryKey: ['getProduct'],
    queryFn: () => {
      const status = params.get('status') as StatusType;
      if (productId && status) return getProduct({ id: productId, status });
      else return null;
    },
    enabled: !!productId && !!params.get('status'),
    staleTime: 0,
  });

  const { mutate: deleteProductMutation, isPending } = useMutation({
    mutationFn: deleteProduct,
    mutationKey: ['getAddress'],
    onSuccess: () => {
      showAlert('Product was deleted!', 'success');
      closePopup();
      navigate(`/brands/${brandId}/products`);
    },
  });

  return (
    <div className="page-container">
      <PageHeader text={text}>
        {product?.status !== 'Revise' ? (
          <button
            type="button"
            className="max-md:ml-auto"
            onClick={() => {
              setAvailable(true);
              openPopup('confirmation');
            }}
            disabled={isPending || isFetching || isFetchingPoint}
          >
            <TrashIcon height={24} width={24} />
          </button>
        ) : null}
      </PageHeader>
      {(isFetching || isFetchingPoint) && <AppLoader />}
      {!isFetching && !isFetchingPoint && brand && product && (
        <ProductForm brandId={brand.id} product={product} />
      )}
      <Popup width={SECOND_POPUP_WIDTH} available={available} onClose={() => setAvailable(false)}>
        {popupName === 'confirmation' && (
          <ConfirmationPopup
            title={t('deleteProduct', { ns: productsTableTranslations.name })}
            onAccept={() =>
              product?.id && brandId && deleteProductMutation({ id: product.id, brandId })
            }
            text={t('confirmationText', { ns: productsTableTranslations.name })}
          />
        )}
      </Popup>
    </div>
  );
}

export default observer(EditProduct);
